import { ResizeNotifier } from '@theme/utilities';
import { DeclarativeShadowElement } from '@theme/component';

export class OverflowMinimumEvent extends Event {

  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.#initialized || !this.isConnected) return;

    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
      return;
    }

    if (name === 'minimum-items' && this.getAttribute('disabled') !== 'true') {
      this.#reflowItems();
    }
  }

  async connectedCallback() {
    super.connectedCallback();

    const connectionEpoch = ++this.#connectionEpoch;

    await this.#waitForStyles();

    if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) return;

    if (!this.#initialized) {
      this.#initialize();
      this.#initialized = true;
    } else if (this.getAttribute('disabled') === 'true') {
      this.#reset();
    } else {
      this.#reflowItems();
    }
  }

  #waitForStyles() {

    const styles = this.shadowRoot?.querySelector('link[rel="stylesheet"]');

    if (!styles || styles.sheet) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const done = () => {
        styles.removeEventListener('load', done);
        styles.removeEventListener('error', done);
        resolve();
      };
      styles.addEventListener('load', done, { once: true });

      styles.addEventListener('error', done, { once: true });
    });
  }

  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    const isSemanticList = list instanceof HTMLUListElement;

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(isSemanticList || list instanceof HTMLDivElement) ||
      !(isSemanticList ? placeholder instanceof HTMLLIElement : placeholder instanceof HTMLDivElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    this.addEventListener(
      'reflow',
        (event) => {
        this.#reflowItems(0, event.detail.lastVisibleElement);
      }
    );

    const elements = defaultSlot.assignedElements().filter((element) => !(element instanceof HTMLTemplateElement));
    const firstElement = elements[0];
    const lastElement = elements[elements.length - 1];

    if (this.getAttribute('disabled') === 'true') {
      this.#reset();
      return;
    }

    if (firstElement) {
      this.#intersectionObserver.observe(firstElement);
    }
    if (lastElement && lastElement !== firstElement) {
      this.#intersectionObserver.observe(lastElement);
    }
  }

  disconnectedCallback() {
    this.#connectionEpoch += 1;
    this.#cancelScheduledWork();
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      :   (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;
  #connectionEpoch = 0;

  #initialized = false;

  #reflowFrame;

  #reflowTimeout;

  #intersectionTimeout;

  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  #handleIntersection = (entries) => {
    const entry = entries[0];
    if (!entry?.isIntersecting) return;

    this.#intersectionObserver.disconnect();
    const connectionEpoch = this.#connectionEpoch;
    this.#intersectionTimeout = setTimeout(() => {
      this.#intersectionTimeout = undefined;
      if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) return;

      this.querySelector(':scope > template[shadowrootmode="open"]')?.remove();
      this.#reflowItems(entry.boundingClientRect.height);
    }, 0);
  };

  #handleChange = () => {
    if (this.#scheduled) return;

    this.#scheduled = true;
    const connectionEpoch = this.#connectionEpoch;
    this.#reflowFrame = requestAnimationFrame(() => {
      this.#reflowFrame = undefined;
      if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) {
        this.#scheduled = false;
        return;
      }

      this.#reflowTimeout = setTimeout(() => {
        this.#reflowTimeout = undefined;
        if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) {
          this.#scheduled = false;
          return;
        }

        this.#reflowItems();
        this.#scheduled = false;
      }, 0);
    });
  };

  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      if (element.slot !== defaultSlot.name) {
        element.slot = defaultSlot.name;
      }
    }
  }

  #reset() {
    const { list } = this.#refs;

    this.#unobserveChanges();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
    this.#setMinimumReached(false);
  }

  #setMinimumReached(minimumReached) {
    const wasMinimumReached = this.hasAttribute('minimum-reached');
    this.toggleAttribute('minimum-reached', minimumReached);
    if (wasMinimumReached !== minimumReached) {
      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  #updateMinimumReached(visibleElements) {
    const minimumItems = this.minimumItems;
    this.#setMinimumReached(minimumItems !== null && visibleElements.length <= minimumItems);
  }

  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  #reflowItems = (listHeight = 0, lastVisibleElement = null) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    if (this.ownerDocument.defaultView?.getComputedStyle(this).display === 'none') {
      this.#observeChanges();
      return;
    }

    this.#unobserveChanges();

    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();
    const lastElement = elements[elements.length - 1];

    if (!lastElement) {
      this.#setMinimumReached(false);
      this.#observeChanges();
      return;
    }

    let visibleElements = [];

    let overflowingElements = [];
    let placeholderWidth = 0;
    let hasOverflow = false;

    if (listHeight > 0) {
      list.style.setProperty('height', `${listHeight}px`);
    }

    list.style.setProperty('flex-wrap', 'wrap');
    placeholder.hidden = true;

    moreSlot.style.setProperty('order', '-1');
    moreSlot.hidden = false;

    lastVisibleElement?.style.setProperty('order', '-1');

    const moreSlotRect = moreSlot.getBoundingClientRect();

    elements.forEach((element) => {
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top > moreSlotRect.top) {
        if (!overflowingElements.length) {
          placeholderWidth = elementRect.width;
        }

        hasOverflow = true;
        overflowingElements.push(element);
      } else {
        visibleElements.push(element);
      }
    });

    if (hasOverflow) {
      moreSlot.style.removeProperty('order');
    }
    lastVisibleElement?.style.removeProperty('order');

    for (const element of elements) {
      const targetSlot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
      if (element.slot !== targetSlot) {
        element.slot = targetSlot;
      }
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);

    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {

      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('overflow', 'unset');

    this.#updateMinimumReached(visibleElements);

    this.#observeChanges();
  };

  #observeChanges() {
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  }

  #unobserveChanges() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }
  #cancelScheduledWork() {
    if (this.#reflowFrame !== undefined) {
      cancelAnimationFrame(this.#reflowFrame);
      this.#reflowFrame = undefined;
    }
    clearTimeout(this.#reflowTimeout);
    this.#reflowTimeout = undefined;
    clearTimeout(this.#intersectionTimeout);
    this.#intersectionTimeout = undefined;
    this.#scheduled = false;
  }

  #refs;

  #resizeObserver = new ResizeNotifier(this.#handleChange);

  #mutationObserver = new MutationObserver(this.#handleChange);

  #intersectionObserver = new IntersectionObserver(this.#handleIntersection, {

    rootMargin: '640px 360px 640px 360px',
  });
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}
